import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import type { PickerDataSource } from "@/app/(v2)/workflows/_components/form-fields/TokenSelector";

export interface CoinGeckoCoin {
  id: string;
  name: string;
  symbol: string;
  marketCapRank: number | null;
  thumb: string;
  large: string;
}

interface SearchResultItem {
  id: string;
  name: string;
  symbol: string;
  marketCapRank?: number | null;
  thumb?: string;
  large?: string;
}

interface MarketsResultItem {
  id: string;
  symbol: string;
  name: string;
  image?: string;
  marketCapRank?: number | null;
}

interface CoinInfoResult {
  coin: {
    id: string;
    name: string;
    symbol: string;
    marketCapRank?: number | null;
    image?: { thumb?: string; small?: string; large?: string };
  };
}

type ProxyEnvelope<T> = {
  code: number;
  message?: string;
  data?: {
    success?: boolean;
    result?: T & { error?: { code?: string; message?: string } };
  };
};

/**
 * POST a payload to b3os-workflow's action-proxy /query endpoint and return the action result.
 *
 * The picker must go through the backend (not api.coingecko.com directly) so the server-side
 * COINGECKO_API_KEY is used, rate-limits are shared across users, and the pattern matches
 * every other workflow form-field fetcher.
 */
async function queryActionProxy<T>(
  actionId: string,
  payload: Record<string, unknown>,
  signal: AbortSignal,
): Promise<T> {
  const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/action-proxy/${actionId}/query`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ payload }),
    signal,
    showErrorToast: false,
  });
  if (!response.ok) {
    throw new Error(`CoinGecko proxy failed: HTTP ${response.status}`);
  }
  const body = (await response.json()) as ProxyEnvelope<T>;
  if (body.code !== 0 || !body.data?.success) {
    const msg = body.data?.result?.error?.message ?? body.message ?? `code=${body.code}`;
    throw new Error(`CoinGecko proxy failed: ${msg}`);
  }
  if (!body.data.result) {
    throw new Error("CoinGecko proxy failed: empty result");
  }
  return body.data.result as T;
}

async function fetchCoinSearch(query: string, signal: AbortSignal): Promise<CoinGeckoCoin[]> {
  const result = await queryActionProxy<{ coins: SearchResultItem[] }>("coingecko-search", { query }, signal);
  return result.coins.map(c => ({
    id: c.id,
    name: c.name,
    symbol: c.symbol,
    marketCapRank: c.marketCapRank ?? null,
    thumb: c.thumb ?? "",
    large: c.large ?? c.thumb ?? "",
  }));
}

async function fetchTopCoins(signal: AbortSignal): Promise<CoinGeckoCoin[]> {
  // Shown when the picker opens before the user types — gives instant recognizable options.
  const result = await queryActionProxy<{ coins: MarketsResultItem[] }>(
    "coingecko-get-coin-markets",
    { vsCurrency: "usd", order: "market_cap_desc", perPage: 20, page: 1 },
    signal,
  );
  return result.coins.map(c => ({
    id: c.id,
    name: c.name,
    symbol: c.symbol,
    marketCapRank: c.marketCapRank ?? null,
    thumb: c.image ?? "",
    large: c.image ?? "",
  }));
}

export function useCoinGeckoSearch(searchTerm: string, enabled = true) {
  const trimmed = searchTerm.trim();
  const hasQuery = trimmed.length > 0;

  const searchQuery = useQuery({
    queryKey: ["coingecko-search", trimmed],
    queryFn: ({ signal }) => fetchCoinSearch(trimmed, signal),
    enabled: enabled && hasQuery,
    staleTime: 5 * 60 * 1000,
  });

  const topQuery = useQuery({
    queryKey: ["coingecko-top-coins"],
    queryFn: ({ signal }) => fetchTopCoins(signal),
    enabled: enabled && !hasQuery,
    staleTime: 10 * 60 * 1000,
  });

  const active = hasQuery ? searchQuery : topQuery;

  return {
    coins: active.data ?? [],
    isLoading: active.isLoading,
    error: (active.error ?? null) as Error | null,
  };
}

export function useDebouncedCoinGeckoSearch(delayMs = 300) {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedTerm(searchTerm), delayMs);
    return () => clearTimeout(t);
  }, [searchTerm, delayMs]);

  const { coins, isLoading, error } = useCoinGeckoSearch(debouncedTerm);

  return { coins, isLoading, error, searchTerm, setSearchTerm };
}

/**
 * CoinGecko data source adapter matching TokenSelector's `PickerDataSource<T>` contract.
 * Pass to `<TokenSelector dataSource={coinGeckoDataSource} ... />` via the
 * `coinGeckoPickerProps` bundle below.
 */
export const coinGeckoDataSource: PickerDataSource<CoinGeckoCoin> = {
  useSearch: (term: string) => {
    const { coins, isLoading, error } = useCoinGeckoSearch(term);
    return { items: coins, isLoading, error };
  },
  useLookup: (id: string | undefined) => {
    const { data } = useCoinGeckoCoin(id);
    return { item: data ?? null };
  },
};

function CoinIcon({ src, alt, size = 20 }: { src: string; alt: string; size?: number }) {
  const [hasError, setHasError] = useState(false);
  useEffect(() => {
    setHasError(false);
  }, [src]);
  if (!src || hasError) {
    return (
      <span
        className="bg-ds-neutral-200 dark:bg-ds-neutral-800 inline-flex items-center justify-center rounded-full text-[10px] font-semibold uppercase"
        style={{ width: size, height: size }}
      >
        {alt.slice(0, 2)}
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      style={{ width: size, height: size }}
      className="rounded-full"
      onError={() => setHasError(true)}
    />
  );
}

/**
 * Ready-to-spread bundle for CoinGecko coin picking on `<TokenSelector>`.
 * Usage: `<TokenSelector {...coinGeckoPickerProps} value={value} onChange={onChange} label="Coin" />`.
 */
export const coinGeckoPickerProps = {
  dataSource: coinGeckoDataSource as PickerDataSource<unknown>,
  getKey: (coin: unknown) => (coin as CoinGeckoCoin).id,
  placeholder: "Search coin (e.g. ethereum, btc, doge)...",
  searchPlaceholder: "Search coins...",
  loadingLabel: "Searching...",
  errorLabel: "CoinGecko lookup failed. Check your connection and try again.",
  emptyLabel: (term: string) => (term.trim() ? "No coins found." : "Type to search CoinGecko."),
  groupHeading: (term: string) => (term.trim() ? "Results" : "Top by market cap"),
  multiAddMorePlaceholder: "Add another coin...",

  renderRow: (item: unknown) => {
    const coin = item as CoinGeckoCoin;
    return (
      <>
        <CoinIcon src={coin.thumb} alt={coin.symbol} size={20} />
        <span className="min-w-0 flex-1 truncate">
          <span className="font-medium">{coin.name}</span>
          <span className="text-ds-text-muted ml-2 text-xs uppercase">{coin.symbol}</span>
        </span>
        {coin.marketCapRank != null && (
          <span className="text-ds-text-disabled ml-2 shrink-0 text-xs">#{coin.marketCapRank}</span>
        )}
      </>
    );
  },

  renderTriggerValue: (item: unknown | null, raw: string) => {
    const coin = item as CoinGeckoCoin | null;
    return (
      <>
        <CoinIcon src={coin?.thumb ?? ""} alt={coin?.symbol ?? raw} size={20} />
        <span className="min-w-0 flex-1 truncate">
          <span className="font-medium">{coin?.name ?? raw}</span>
          {coin?.symbol && <span className="text-ds-text-muted ml-2 text-xs uppercase">{coin.symbol}</span>}
        </span>
      </>
    );
  },

  renderChip: ({ item, id, onRemove }: { item: unknown | null; id: string; onRemove: () => void }) => {
    const coin = item as CoinGeckoCoin | null;
    return (
      <div className="border-ds-border-default dark:bg-ds-neutral-800 flex items-center gap-1.5 rounded-lg border bg-zinc-50 px-2 py-1 text-sm">
        <CoinIcon src={coin?.thumb ?? ""} alt={coin?.symbol ?? id} size={16} />
        <span className="text-ds-text-primary font-medium">{coin?.name ?? id}</span>
        {coin?.symbol && <span className="text-ds-text-muted text-xs uppercase">{coin.symbol}</span>}
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${coin?.name ?? id}`}
          className="text-ds-text-disabled hover:bg-ds-neutral-200 hover:text-ds-text-subtle dark:hover:bg-ds-neutral-700 ml-1 rounded-full p-0.5 transition-colors dark:hover:text-zinc-300"
        >
          <svg
            className="h-3 w-3"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="M3 3l6 6M9 3l-6 6" />
          </svg>
        </button>
      </div>
    );
  },
};

/**
 * Fetch metadata for a single coin ID — used by the picker to render the
 * selected chip (name + thumb) when the id was set without going through
 * the search flow (e.g. AI-generated prop defaults, template-authored values).
 */
export function useCoinGeckoCoin(coinId: string | undefined) {
  return useQuery({
    queryKey: ["coingecko-coin", coinId],
    queryFn: async ({ signal }): Promise<CoinGeckoCoin | null> => {
      if (!coinId) return null;
      try {
        const result = await queryActionProxy<CoinInfoResult>(
          "coingecko-get-coin-info",
          {
            coinId,
            localization: false,
            tickers: false,
            marketData: false,
            communityData: false,
            developerData: false,
            sparkline: false,
          },
          signal,
        );
        const c = result.coin;
        return {
          id: c.id,
          name: c.name,
          symbol: c.symbol,
          marketCapRank: c.marketCapRank ?? null,
          thumb: c.image?.thumb ?? c.image?.small ?? "",
          large: c.image?.large ?? c.image?.small ?? c.image?.thumb ?? "",
        };
      } catch {
        // Missing coin id is not an error for the picker — render falls back to the raw id.
        return null;
      }
    },
    enabled: !!coinId,
    staleTime: 60 * 60 * 1000,
  });
}
